import type { CalendarClient } from '@wtfalch/calendar/calendar';
import type { ChatClient } from '@wtfalch/chat';
import { Button, Empty, Icon, Panes } from '@wtfalch/design';
import type {
  Attachment,
  Draft,
  DraftAttachment,
  ListOptions,
  MailboxNode,
  MailClient,
  MailError,
  MailIdentity,
  Message,
  Sent,
  ThreadDetail,
  ThreadPage,
  VacationResponse,
} from '@wtfalch/email/mailbox';
import {
  cancel as cancelSend,
  createMailbox,
  downloadAttachment,
  identities as fetchIdentities,
  thread as fetchThread,
  findRole,
  listLabels,
  mailboxes,
  moveThread,
  moveToRole,
  queryThreads,
  replyDraft,
  send as sendMail,
  setFlagged,
  setLabel,
  setRead,
  snoozeThread,
  toMailError,
  vacationResponse,
  walk,
} from '@wtfalch/email/mailbox';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { MailLocation } from '../routes.ts';
import { isoToDay } from './awayDates.ts';
import { Compose, type ComposeInitial } from './Compose.tsx';
import { LIST_PAGE_SIZE } from './format.ts';
import { MailFolders, type SharedGroup } from './MailFolders.tsx';
import { MailList } from './MailList.tsx';
import { MailSearchResults } from './MailSearchResults.tsx';
import { MailSettings, type MailSettingsTab } from './MailSettings.tsx';
import { ThreadPane } from './ThreadPane.tsx';
import { usePhone } from './usePhone.ts';
import './mail.css';

function listFilter(mailboxId: string, unreadOnly: boolean) {
  return { inMailbox: mailboxId, ...(unreadOnly ? { notKeyword: '$seen' } : {}) };
}

function listOptions(sort: 'newest' | 'oldest', position: number): ListOptions {
  return {
    sort: [{ property: 'receivedAt', isAscending: sort === 'oldest' }],
    position,
    limit: LIST_PAGE_SIZE,
    calculateTotal: true,
  };
}

/**
 * The Mail section: the list column and the thread pane it opens beside,
 * matching `Mail.dc.html`, on `@wtfalch/email/mailbox` alone.
 *
 * `onActions` reports the header's own control -- the "Write" button the
 * board draws at the top right of the card, in `AgoraShell`'s `actions`
 * slot -- the same way `App.tsx` already lifts `onSignOutChange` and
 * `onChatAvailable` out of a section. Compose is not a route: `MailLocation`
 * has no place for it, and writing a new mail is a mode this section is in,
 * not a place it has navigated to.
 */
export function Mail({
  client: ownClient,
  clientFor,
  calendarClient,
  chatClient,
  agoraId,
  trustedAuthServId,
  location,
  onNavigate,
  onError,
  onActions,
  onTitle,
  initialSettings,
}: {
  client: MailClient;
  /** A client for another account the login reaches (a colleague's shared
   *  folders live in theirs). Unset: shared folders are not offered. Keep it
   *  stable between renders. */
  clientFor?: (accountId: string) => MailClient;
  /** The calendar client already in the app (`App.tsx`'s own
   *  `calendarClient`) -- `ThreadPane`'s `CounterCard` (agora#284) reads and
   *  writes the event a received `COUNTER` proposes a new time for. */
  calendarClient: CalendarClient;
  /** Lets the composer write a chat message too (agora#217); `null` or
   *  unset while Chat is off for this account. */
  chatClient?: ChatClient | null;
  /** For the full-results view's own rows (agora#269's `MailSearchResults`),
   *  which -- like every combined-search row (`search/mail.ts`'s
   *  `routeFor`) -- carries an `AgoraRoute` rather than a bare
   *  `MailLocation`. */
  agoraId: string;
  /** The mail server's authserv-id (`Agora.mailAuthServId`), passed to
   *  `thread()` so `Message.senderAuthenticated` can be true (agora#348).
   *  Unset: never true. */
  trustedAuthServId?: string;
  location: MailLocation;
  onNavigate: (location: MailLocation) => void;
  onError?: (error: MailError) => void;
  onActions?: (actions: ReactNode) => void;
  /** Lifts the header's title to "Write" while the composer is open, the
   *  way the design canvas's own `Compose.dc.html` titles it -- the same
   *  slot pattern `onActions` already uses for the header's "Write"
   *  button. */
  onTitle?: (title: string | null) => void;
  /** Opens on the mail settings, at this tab (`/__preview` states). */
  initialSettings?: MailSettingsTab;
}) {
  const [tree, setTree] = useState<readonly MailboxNode[] | null>(null);
  const [identityList, setIdentityList] = useState<readonly MailIdentity[]>([]);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState<ThreadPage | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<MailError | null>(null);
  const [listKey, setListKey] = useState(0);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [detailError, setDetailError] = useState<MailError | null>(null);
  const [detailKey, setDetailKey] = useState(0);
  const [composing, setComposing] = useState(false);
  const [composeInitial, setComposeInitial] = useState<ComposeInitial | undefined>(undefined);
  const [settings, setSettings] = useState<MailSettingsTab | null>(initialSettings ?? null);
  const [away, setAway] = useState<VacationResponse | null>(null);
  const [sharedAccount, setSharedAccount] = useState<string | null>(null);
  const [ownTree, setOwnTree] = useState<readonly MailboxNode[] | null>(null);
  const [sharedGroups, setSharedGroups] = useState<readonly SharedGroup[]>([]);
  const accountClients = useRef(new Map<string, MailClient>());
  const [busyThread, setBusyThread] = useState(false);
  const [allLabels, setAllLabels] = useState<readonly string[]>([]);

  const phone = usePhone();
  const me = identityList[0]?.email ?? '';

  const report = useCallback(
    (err: unknown) => {
      const mailError = toMailError(err);
      onError?.(mailError);
      return mailError;
    },
    [onError],
  );

  // The client for the folder being read: the login's own, or the account a
  // shared folder lives in. Everything the person writes or sets up stays on
  // `ownClient`.
  const client = useMemo(() => {
    if (!sharedAccount || !clientFor) return ownClient;
    let other = accountClients.current.get(sharedAccount);
    if (!other) {
      other = clientFor(sharedAccount);
      accountClients.current.set(sharedAccount, other);
    }
    return other;
  }, [ownClient, clientFor, sharedAccount]);

  useEffect(() => {
    if (!sharedAccount && tree) setOwnTree(tree);
  }, [sharedAccount, tree]);

  // Folders other accounts have shared with this login. A failed read leaves
  // the list without that account.
  useEffect(() => {
    if (!clientFor) return;
    let live = true;
    (async () => {
      const groups: SharedGroup[] = [];
      for (const account of await ownClient.accounts()) {
        if (account.isPersonal) continue;
        try {
          const folders = [...walk(await mailboxes(clientFor(account.id)))].filter(
            (m) => m.myRights.mayReadItems,
          );
          if (folders.length) groups.push({ accountId: account.id, name: account.name, folders });
        } catch (err) {
          console.warn(`Mail: could not read the folders of ${account.name}`, err);
        }
      }
      if (live) setSharedGroups(groups);
    })().catch((err: unknown) => {
      console.warn('Mail: could not list shared accounts', err);
    });
    return () => {
      live = false;
    };
  }, [ownClient, clientFor]);

  // The mailbox tree, once.
  useEffect(() => {
    let live = true;
    mailboxes(client)
      .then((t) => live && setTree(t))
      .catch((err) => live && report(err));
    return () => {
      live = false;
    };
  }, [client, report]);

  // Whether an away reply is running, for the banner. A failed read only
  // leaves the banner out.
  useEffect(() => {
    let live = true;
    vacationResponse(ownClient)
      .then((v) => live && setAway(v))
      .catch((err: unknown) => {
        if (live) setAway(null);
        console.warn('Mail: could not read the away reply', err);
      });
    return () => {
      live = false;
    };
  }, [ownClient]);

  // The sending identities, once.
  useEffect(() => {
    let live = true;
    fetchIdentities(ownClient)
      .then((ids) => live && setIdentityList(ids))
      .catch((err) => live && report(err));
    return () => {
      live = false;
    };
  }, [ownClient, report]);

  // The labels in use, for the toolbar's Label menu. Read again with every
  // list refresh, since a label is added or dropped by a mutation. A failed
  // read leaves the menu with the open thread's own labels only.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `listKey` is the trigger
  useEffect(() => {
    let live = true;
    listLabels(client)
      .then((found) => live && setAllLabels(found ?? []))
      .catch((err) => live && report(err));
    return () => {
      live = false;
    };
  }, [client, listKey, report]);

  // No mailbox in the address yet: land in the Inbox, the way Today does
  // for the section as a whole. Not while showing the full-results view
  // (agora#269), which also has no `mailboxId` of its own -- `query` set
  // means "showing search results", not "no mailbox chosen yet", and
  // redirecting here would silently drop the query out of the address the
  // moment the mailbox tree finished loading.
  useEffect(() => {
    if (!tree || location.mailboxId || location.query !== undefined) return;
    const inbox = findRole(tree, 'inbox');
    // A thread already in the address (a search hit with no mailbox of its
    // own) stays open in the Inbox.
    if (inbox) onNavigate({ mailboxId: inbox.id, threadId: location.threadId });
  }, [tree, location.mailboxId, location.threadId, location.query, onNavigate]);

  // Another folder's rows are gone the moment it is chosen, not once its own
  // arrive: they would stay clickable under the wrong name meanwhile.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the mailbox id is the trigger
  useEffect(() => {
    setPage(null);
  }, [location.mailboxId]);

  // The thread list: refetched on the mailbox, the sort, the filter, or a
  // `listKey` bump from a mutation elsewhere in this section. `listKey`
  // itself is read for no reason but to be a real dependency -- it exists
  // purely to retrigger this effect after a mutation. One page of
  // `LIST_PAGE_SIZE`; `loadMore` appends the next.
  useEffect(() => {
    if (!location.mailboxId) {
      setPage(null);
      return;
    }
    void listKey;
    let live = true;
    setListLoading(true);
    setListError(null);
    queryThreads(client, listFilter(location.mailboxId, unreadOnly), listOptions(sort, 0))
      .then((p) => live && setPage(p))
      .catch((err) => live && setListError(report(err)))
      .finally(() => live && setListLoading(false));
    return () => {
      live = false;
    };
  }, [client, location.mailboxId, sort, unreadOnly, listKey, report]);

  const loadMore = useCallback(async () => {
    if (!location.mailboxId || !page) return;
    setListLoading(true);
    try {
      const next = await queryThreads(
        client,
        listFilter(location.mailboxId, unreadOnly),
        listOptions(sort, page.items.length),
      );
      setPage((prev) => (prev ? { ...next, items: [...prev.items, ...next.items] } : prev));
    } catch (err) {
      setListError(report(err));
    } finally {
      setListLoading(false);
    }
  }, [client, location.mailboxId, page, sort, unreadOnly, report]);

  // The open thread: refetched on the thread id, or a `detailKey` bump
  // after a reply or a star toggle. Opening a thread that has anything
  // unread marks it read, the way selecting it means "I have seen this".
  useEffect(() => {
    if (!location.threadId) {
      setDetail(null);
      return;
    }
    void detailKey;
    let live = true;
    setDetailError(null);
    const threadId = location.threadId;
    fetchThread(client, threadId, { trustedAuthServId })
      .then((d) => {
        if (!live) return;
        setDetail(d);
        if (d.messages.some((m) => m.isUnread)) {
          setRead(client, threadId, true)
            .then(() => live && setListKey((k) => k + 1))
            .catch((err) => report(err));
        }
      })
      .catch((err) => live && setDetailError(report(err)));
    return () => {
      live = false;
    };
  }, [client, location.threadId, detailKey, report, trustedAuthServId]);

  const startWriting = useCallback(() => {
    setComposeInitial(undefined);
    setComposing(true);
  }, []);

  // No "Write" while writing: the composer is already open, and pressing the
  // button would only reset the address it was opened for. On a phone the
  // list carries its own floating Write instead of the header's.
  useEffect(() => {
    if (composing || phone) {
      onActions?.(null);
      return;
    }
    onActions?.(
      // `@wtfalch/design`'s own `Button`, like every primary in the app.
      <Button kind="primary" onPress={startWriting}>
        <Icon name="pen" size={18} />
        <span>Write</span>
      </Button>,
    );
    return () => onActions?.(null);
  }, [onActions, composing, phone, startWriting]);

  useEffect(() => {
    onTitle?.(composing ? 'Write' : null);
    return () => onTitle?.(null);
  }, [composing, onTitle]);

  // `/h/<agoraId>/mail/new` (Today's own "Write" button, and any other link
  // that wants to land straight in the composer) -- a one-shot trigger read
  // once per navigation, not a route this section stays synced to: compose
  // is a mode, not a place (see this file's own docblock), so closing it
  // does not navigate back out of `/mail/new`.
  // Keyed on the address too: a second `/mail/new/<addr>` after a cancel (or
  // while composing) opens the composer for that address.
  const openedComposeRef = useRef<string | null>(null);
  useEffect(() => {
    const key = location.compose ? (location.to ?? '') : null;
    if (key !== null && openedComposeRef.current !== key) {
      openedComposeRef.current = key;
      setComposeInitial(location.to ? { to: location.to } : undefined);
      setComposing(true);
    }
    if (key === null) openedComposeRef.current = null;
  }, [location.compose, location.to]);

  const refreshList = useCallback(() => setListKey((k) => k + 1), []);

  const handleArchive = useCallback(
    async (threadId: string) => {
      setBusyThread(true);
      try {
        await moveToRole(client, threadId, 'archive', { mailboxes: tree ?? undefined });
        onNavigate({ mailboxId: location.mailboxId, threadId: null });
        refreshList();
      } catch (err) {
        report(err);
      } finally {
        setBusyThread(false);
      }
    },
    [client, tree, location.mailboxId, onNavigate, refreshList, report],
  );

  const handleTrash = useCallback(
    async (threadId: string) => {
      setBusyThread(true);
      try {
        await moveToRole(client, threadId, 'trash', { mailboxes: tree ?? undefined });
        onNavigate({ mailboxId: location.mailboxId, threadId: null });
        refreshList();
      } catch (err) {
        report(err);
      } finally {
        setBusyThread(false);
      }
    },
    [client, tree, location.mailboxId, onNavigate, refreshList, report],
  );

  const handleStar = useCallback(
    async (threadId: string, flagged: boolean) => {
      try {
        await setFlagged(client, threadId, flagged);
        setDetailKey((k) => k + 1);
        refreshList();
      } catch (err) {
        report(err);
      }
    },
    [client, refreshList, report],
  );

  const handleMarkUnread = useCallback(
    async (threadId: string) => {
      try {
        await setRead(client, threadId, false);
        onNavigate({ mailboxId: location.mailboxId, threadId: null });
        refreshList();
      } catch (err) {
        report(err);
      }
    },
    [client, location.mailboxId, onNavigate, refreshList, report],
  );

  const handleMoveTo = useCallback(
    async (threadId: string, mailboxId: string) => {
      try {
        await moveThread(client, threadId, mailboxId);
        onNavigate({ mailboxId: location.mailboxId, threadId: null });
        refreshList();
      } catch (err) {
        report(err);
      }
    },
    [client, location.mailboxId, onNavigate, refreshList, report],
  );

  /** Snoozing files the thread into a Snoozed mailbox that is made on first
   *  use, so the folder tree is read again to show it. */
  const handleSnooze = useCallback(
    async (threadId: string, until: Date) => {
      setBusyThread(true);
      try {
        await snoozeThread(client, threadId, until, { mailboxes: tree ?? undefined });
        setTree(await mailboxes(client));
        onNavigate({ mailboxId: location.mailboxId, threadId: null });
        refreshList();
      } catch (err) {
        report(err);
      } finally {
        setBusyThread(false);
      }
    },
    [client, tree, location.mailboxId, onNavigate, refreshList, report],
  );

  const handleLabel = useCallback(
    async (threadId: string, label: string, on: boolean): Promise<string | null> => {
      try {
        await setLabel(client, threadId, label, on);
        refreshList();
        return null;
      } catch (err) {
        return report(err).message;
      }
    },
    [client, refreshList, report],
  );

  /** The folder column's own `+` (agora#287). Opens the new folder the way
   *  choosing any other mailbox does, once the server has actually made it
   *  -- there is nothing optimistic here for the same reason `mutate.ts`'s
   *  own docblock gives every other change in this file: a folder drawn
   *  before the server agrees is a folder that may vanish on refresh. */
  const handleCreateFolder = useCallback(
    async (name: string) => {
      const made = await createMailbox(ownClient, name).catch((err) => {
        throw report(err);
      });
      const nextTree = await mailboxes(ownClient).catch((err) => {
        throw report(err);
      });
      setSharedAccount(null);
      setTree(nextTree);
      onNavigate({ mailboxId: made.id, threadId: null });
    },
    [ownClient, onNavigate, report],
  );

  /** Ten seconds held server-side (RFC 4865's `holdFor`, through `send`'s
   *  own `delay` option) before a reply actually goes out -- the canvas's
   *  own "Sends in 10 seconds — time to change your mind." The email is
   *  already filed into Sent the moment this call returns (`send()`'s own
   *  docblock: `onSuccessUpdateEmail` runs unconditionally), so the thread
   *  refresh below happens right away; only the outbound transmission is
   *  held, and `cancel()` reverts that same filing if the window is still
   *  open when `ThreadPane` calls it. */
  const REPLY_HOLD_SECONDS = 10;

  const handleReply = useCallback(
    async (
      message: Message,
      text: string,
      all: boolean,
      attachments: readonly DraftAttachment[],
    ): Promise<{ cancel: () => Promise<void> }> => {
      const identity = identityList[0];
      if (!identity) throw new Error('Your sending address has not loaded yet. Try again.');
      const draft = replyDraft(message, { identity, all });
      try {
        // The own login's client and folders, even inside a shared folder:
        // the reply goes out from this person, not from the sharing account.
        const sent: Sent = await sendMail(
          ownClient,
          {
            ...draft,
            text: `${text}${draft.text ?? ''}`,
            ...(attachments.length ? { attachments } : {}),
          },
          { identity, mailboxes: ownTree ?? undefined, delay: REPLY_HOLD_SECONDS },
        );
        setDetailKey((k) => k + 1);
        refreshList();
        return {
          cancel: async () => {
            await cancelSend(ownClient, sent, { mailboxes: ownTree ?? undefined });
            setDetailKey((k) => k + 1);
            refreshList();
          },
        };
      } catch (err) {
        report(err);
        throw err;
      }
    },
    [ownClient, identityList, ownTree, refreshList, report],
  );

  const handleComposeSend = useCallback(
    async (draft: Draft, options?: { delay?: number }) => {
      try {
        await sendMail(ownClient, draft, {
          mailboxes: ownTree ?? undefined,
          ...(options?.delay ? { delay: options.delay } : {}),
        });
        setComposing(false);
        setComposeInitial(undefined);
        refreshList();
        // Leave `/mail/new[/addr]`, as closing does: a reload must not
        // reopen an empty composer, and the same link must open it again.
        if (location.compose) onNavigate({ mailboxId: location.mailboxId, threadId: null });
      } catch (err) {
        report(err);
        throw err;
      }
    },
    [ownClient, ownTree, refreshList, report, location.compose, location.mailboxId, onNavigate],
  );

  /** `ThreadPane`'s own expand button: opens the full composer, seeded with
   *  the reply's current recipients, subject and text-so-far, so switching
   *  to the full screen never loses what was already written. The open
   *  thread's own ids ride along too (agora#480), so an invitation made
   *  from here can link back to it. */
  const handleExpandReply = useCallback(
    (initial: ComposeInitial) => {
      setComposeInitial({
        ...initial,
        ...(location.mailboxId && location.threadId
          ? { sourceMailboxId: location.mailboxId, sourceThreadId: location.threadId }
          : {}),
      });
      setComposing(true);
    },
    [location.mailboxId, location.threadId],
  );

  const handleDownload = useCallback(
    async (attachment: Attachment) => {
      try {
        const blob = await downloadAttachment(client, attachment);
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = attachment.name;
        // In the document and revoked late: Firefox and Safari start the
        // download after `click()` returns, and a revoked URL gives them
        // an empty file.
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        report(err);
      }
    },
    [client, report],
  );

  // The full-results view (agora#269): reached at
  // `/h/<agoraId>/mail/search/<query>`, what Enter opens from the header
  // search when nothing is highlighted. Checked ahead of `composing`: the
  // address bar is the state (`routes.ts`'s own doc), so a location that
  // says search wins over whatever mode this section was already in --
  // the same as opening a thread while composing would, had `composing`
  // read `location` at all instead of being purely local state.
  if (location.query !== undefined) {
    return (
      <div className="mail contents">
        <MailSearchResults
          client={client}
          agoraId={agoraId}
          query={location.query}
          onOpen={(row) => {
            if (row.route.section === 'mail') onNavigate(row.route.location);
          }}
        />
      </div>
    );
  }

  if (settings && !composing) {
    return (
      <Panes className="mail mail-thread-open flex-1 min-w-0">
        <MailSettings
          client={ownClient}
          tab={settings}
          onTab={setSettings}
          onClose={() => setSettings(null)}
          onAwayChange={setAway}
          tree={ownTree}
          onTreeChange={(next) => {
            setOwnTree(next);
            if (!sharedAccount) setTree(next);
          }}
        />
      </Panes>
    );
  }

  if (composing) {
    return (
      // `mail-thread-open`: below 1150 `mail.css` hides the reader column
      // until one is open, and the composer is that column.
      <Panes className="mail mail-thread-open flex-1 min-w-0">
        <Compose
          // A second `/mail/new/<address>` while writing starts over for it.
          key={`${composeInitial?.to ?? ''}|${composeInitial?.subject ?? ''}`}
          client={ownClient}
          identities={identityList}
          initial={composeInitial}
          calendarClient={calendarClient}
          chatClient={chatClient}
          agoraId={agoraId}
          onSend={handleComposeSend}
          onCancel={() => {
            setComposing(false);
            setComposeInitial(undefined);
            // Leave `/mail/new/<addr>` so the same link (a person card's
            // Mail, clicked again) is a new navigation that reopens it.
            if (location.compose) onNavigate({ mailboxId: location.mailboxId, threadId: null });
          }}
        />
      </Panes>
    );
  }

  const threadOpen = Boolean(location.threadId);

  return (
    <Panes className={threadOpen ? 'mail mail-thread-open flex-1 min-w-0' : 'mail flex-1 min-w-0'}>
      <MailFolders
        tree={ownTree}
        mailboxId={location.mailboxId}
        shared={sharedGroups}
        sharedAccount={sharedAccount}
        onSelectMailbox={(id) => {
          if (sharedAccount) {
            setSharedAccount(null);
            setTree(ownTree);
          }
          onNavigate({ mailboxId: id, threadId: null });
        }}
        onSelectShared={(accountId, id) => {
          setTree(null);
          setSharedAccount(accountId);
          onNavigate({ mailboxId: id, threadId: null });
        }}
        onCreateFolder={handleCreateFolder}
        onSettings={() => setSettings('away')}
      />
      <MailList
        tree={tree}
        mailboxId={location.mailboxId}
        page={page}
        loading={listLoading}
        error={listError}
        sort={sort}
        onSortChange={setSort}
        unreadOnly={unreadOnly}
        onUnreadOnlyChange={setUnreadOnly}
        selectedThreadId={location.threadId}
        me={me}
        onSelectMailbox={(id) => onNavigate({ mailboxId: id, threadId: null })}
        onSelectThread={(id) => onNavigate({ mailboxId: location.mailboxId, threadId: id })}
        onRefresh={refreshList}
        onLoadMore={() => void loadMore()}
        onWrite={phone ? startWriting : undefined}
        away={
          away?.isEnabled
            ? { until: isoToDay(away.toDate) || null, onOpen: () => setSettings('away') }
            : null
        }
        onSettings={() => setSettings('away')}
      />
      {threadOpen ? (
        <ThreadPane
          key={location.threadId}
          // The previous thread's detail is not this one's: the reader
          // shows "Loading…" until the right one arrives.
          detail={detail?.id === location.threadId ? detail : null}
          error={detailError}
          me={me}
          meName={identityList[0]?.name.trim() || null}
          identity={identityList[0] ?? null}
          tree={tree}
          client={client}
          ownClient={ownClient}
          calendarClient={calendarClient}
          onBack={() => onNavigate({ mailboxId: location.mailboxId, threadId: null })}
          onArchive={handleArchive}
          onTrash={handleTrash}
          onStar={handleStar}
          onMarkUnread={handleMarkUnread}
          onMoveTo={handleMoveTo}
          onSnooze={handleSnooze}
          labels={page?.items.find((t) => t.id === location.threadId)?.labels ?? []}
          allLabels={allLabels}
          onLabel={handleLabel}
          onReply={handleReply}
          onExpand={handleExpandReply}
          onDownload={handleDownload}
          onRetry={() => setDetailKey((k) => k + 1)}
          busy={busyThread}
        />
      ) : (
        <Panes.Column flex className="mail-reader">
          <div className="grid flex-1 place-items-center p-8 mail-empty-pane">
            <Empty icon="mail">Select a conversation to read it.</Empty>
          </div>
        </Panes.Column>
      )}
    </Panes>
  );
}
